import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import apiService from '../services/api';
import { LoginForm, RegisterForm, ExtendedRegisterForm } from '../types';
import { setSession, clearSession, scheduleAutoLogout, getTokenIfValid } from '../services/session';
import { AUTH_CONFIG } from '../config/api';
import { tieneAcceso, primeraPestanaPermitida, APP } from '../utils/authRoles';

// Mensaje para una cuenta cuyo rol no tiene acceso al SISI (app.sisi, configurable en
// /configuracion/roles) o no tiene ninguna pestaña del SISI. El backend ya rechaza el
// login con 403 cuando se le manda app: 'sisi'; esto cubre sesiones abiertas antes de un
// cambio de rol (SisiRoute en App.tsx) y un backend que todavía no valide `app`.
const MENSAJE_SIN_ACCESO_PLATAFORMA = 'No tienes acceso a esta plataforma. Por favor, contáctate con el administrador.';

export const useAuth = () => {
  const navigate = useNavigate();
  const { 
    user, 
    token, 
    isAuthenticated, 
    isLoading, 
    error,
    login: loginStore, 
    logout: logoutStore, 
    setLoading, 
    setError, 
    clearError 
  } = useAuthStore();

  // Cierra la sesión del SISI principal y deja el mensaje de "sin acceso" para que lo
  // muestre LoginPage. Se usa desde dos lados: el login mismo (abajo) y SisiRoute
  // (App.tsx), que además cubre las sesiones abiertas antes de que existiera el bloqueo.
  const bloquearAccesoPlataforma = () => {
    logoutStore();
    clearSession();
    setError(MENSAJE_SIN_ACCESO_PLATAFORMA);
  };

  const enrichUserWithPermissions = async (baseUser: any) => {
    try {
      const permissionsResponse = await apiService.getAuthPermissions();
      const permissionData = permissionsResponse.data;
      return {
        ...baseUser,
        rol: permissionData?.rol || baseUser?.rol,
        perfil: permissionData?.perfil || baseUser?.perfil,
        permissions: Array.isArray(permissionData?.permissions) ? permissionData.permissions : baseUser?.permissions,
      };
    } catch (error) {
      console.warn('No se pudieron cargar permisos RBAC, usando fallback por rol:', error);
      return baseUser;
    }
  };

  // Query para obtener usuario actual
  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => apiService.getCurrentUser(),
    enabled: false, // Deshabilitamos la consulta automática
    retry: false,
  });

  // Mutation para login
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginForm) => apiService.login(credentials, 'sisi'),
    retry: false,
    onSuccess: async (response) => {
      // El backend devuelve directamente: { user: User, token: string, message: string }
      // No está envuelto en ApiResponse
      if (response.user && response.token) {
        // La sesión (localStorage) se escribe primero porque el interceptor de axios lee
        // el token desde ahí para GET /auth/permissions. El store de React, en cambio, se
        // actualiza recién DESPUÉS de validar el rol: LoginPage redirige a /dashboard
        // apenas isAuthenticated pasa a true, así que hacerlo antes del await sacaba al
        // usuario de la pantalla —vía SisiRoute, rumbo a /pre-sisi— antes de que se
        // alcanzara a mostrar el mensaje de "sin acceso" de más abajo.
        setSession(response.token, response.user, AUTH_CONFIG.SESSION_DURATION_MINUTES);
        const enrichedUser = await enrichUserWithPermissions(response.user);

        // Rol sin acceso al SISI o sin ninguna pestaña en él: se revierte la sesión recién
        // guardada arriba y se corta acá, en vez de dejarla pasar y bloquearla en SisiRoute.
        const destino = primeraPestanaPermitida(enrichedUser);
        if (!tieneAcceso(enrichedUser, APP.SISI) || !destino) {
          bloquearAccesoPlataforma();
          setLoading(false);
          return;
        }

        loginStore(enrichedUser, response.token);
        setSession(response.token, enrichedUser, AUTH_CONFIG.SESSION_DURATION_MINUTES);
        setLoading(false);

        // Programar auto-logout
        scheduleAutoLogout(() => {
          logoutStore();
          clearSession();
          navigate('/login');
        });

        navigate(destino);
      } else {
        console.error('Respuesta del servidor inválida:', response);
        console.error('User:', response.user);
        console.error('Token:', response.token);
        setError('Respuesta inválida del servidor');
        setLoading(false);
      }
    },
    onError: (error: any) => {
      console.error('Error en login:', error);
      // Helper: extract a user-friendly message from many possible error shapes
      const extractMessage = (err: any): string | null => {
        if (!err) return null;
        if (typeof err === 'string') return err;
        // Axios response body
        const resp = err.response?.data || err.data || null;
        if (!resp) return err.message || null;
        if (typeof resp === 'string') return resp;
        if (typeof resp.message === 'string' && resp.message.trim()) return resp.message;
        if (typeof resp.error === 'string' && resp.error.trim()) return resp.error;
        // Common API shapes: { errors: [{ message: '...' }] } or { errors: { field: ['msg'] } }
        if (Array.isArray(resp.errors) && resp.errors[0]?.message) return resp.errors[0].message;
        if (resp.errors && typeof resp.errors === 'object') {
          const first = Object.values(resp.errors)[0] as any;
          if (Array.isArray(first) && first[0]) return first[0];
          if (typeof first === 'string') return first;
          if (first && typeof first === 'object' && first.message) return first.message;
        }
        // Fallback to JSON-stringified resp if it's informative
        try {
          const s = JSON.stringify(resp);
          if (s && s !== '{}' && s !== 'null') return s;
        } catch (e) {}
        return err.message || null;
      };

      const status = error?.response?.status || error?.status || error?.request?.status;
      const extracted = extractMessage(error) || null;
      // Prefer backend-provided message when present;
      // Handle specific status codes with friendly fallbacks.
      if (status === 401) {
        setError(extracted || 'no es tu contraseña intenta de nuevo');
      } else if (status === 423) {
        // Locked - account blocked due to multiple failed attempts
        setError(
          extracted ||
            'Cuenta bloqueada temporalmente por intentos fallidos. Intenta de nuevo más tarde o contacta a soporte.'
        );
      } else {
        setError(extracted || 'Error al iniciar sesión');
      }
      // Log response body if present for debugging
      if (error?.response?.data) console.error('Login error response data:', error.response.data);
      setLoading(false);
    },
  });

  // Mutation para registro
  const registerMutation = useMutation({
    mutationFn: (userData: RegisterForm) => apiService.register(userData),
    onSuccess: (response) => {
      // El backend devuelve directamente: { user: User, token: string, message: string }
      if (response.user && response.token) {
        loginStore(response.user, response.token);
        setSession(response.token, response.user, AUTH_CONFIG.SESSION_DURATION_MINUTES);
        setLoading(false);
        scheduleAutoLogout(() => {
          logoutStore();
          clearSession();
          navigate('/login');
        });
        navigate('/dashboard');
      } else {
        setError('Respuesta inválida del servidor');
        setLoading(false);
      }
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Error al registrar usuario');
      setLoading(false);
    },
  });

  // Mutation para registro con personal disponible
  const registerWithPersonalMutation = useMutation({
    mutationFn: (userData: ExtendedRegisterForm) => apiService.registerWithPersonalDisponible(userData),
    onSuccess: (response) => {
      if (response.success && response.data) {
        loginStore(response.data.user, response.data.token);
        setSession(response.data.token, response.data.user, AUTH_CONFIG.SESSION_DURATION_MINUTES);
        scheduleAutoLogout(() => {
          logoutStore();
          clearSession();
          navigate('/login');
        });
        navigate('/dashboard');
      }
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Error al registrar usuario y datos de personal');
    },
  });

  // Mutation para logout
  const logoutMutation = useMutation({
    mutationFn: () => apiService.logout(),
    onSuccess: () => {
      logoutStore();
      clearSession();
      navigate('/login');
    },
    onError: () => {
      // Aún así hacemos logout local
      logoutStore();
      clearSession();
      navigate('/login');
    },
  });

  // Al montar el hook: si la sesión expiró, forzar logout; además programar auto-logout si hay sesión válida
  useEffect(() => {
    const token = getTokenIfValid();
    if (!token && isAuthenticated) {
      logoutStore();
      clearSession();
      navigate('/login');
      return;
    }

    // Refrescar SIEMPRE rol y permisos contra el backend al montar, no sólo cuando
    // faltan. La sesión guarda el array `permissions` del login, así que un cambio de
    // rol hecho después (overlay zc_temp.usuario_rol_override) quedaba invisible hasta
    // que la persona cerrara sesión a mano — caso real: rol `planificacion` concedido y
    // el módulo Programación seguía oculto.
    if (token && isAuthenticated && user) {
      enrichUserWithPermissions(user).then((enrichedUser) => {
        const antes = JSON.stringify([(user as any).rol, (user as any).permissions ?? null]);
        const despues = JSON.stringify([enrichedUser?.rol, enrichedUser?.permissions ?? null]);
        if (antes === despues) return;
        loginStore(enrichedUser, token);
        setSession(token, enrichedUser, AUTH_CONFIG.SESSION_DURATION_MINUTES);
      });
    }

    // Solo si hay sesión válida: sin token, remainingMs() es 0 y scheduleAutoLogout
    // ejecuta el logout AL INSTANTE, en el montaje. Eso vaciaba el store (logout() deja
    // error en null) y se llevaba puesto el mensaje de "sin acceso" recién seteado por
    // bloquearAccesoPlataforma, que nunca alcanzaba a pintarse en LoginPage. Mismo
    // criterio que el boot de usePreSisiAuth, que ya condiciona su scheduleAutoLogout.
    if (!token) return;

    const cleanup = scheduleAutoLogout(() => {
      logoutStore();
      clearSession();
      navigate('/login');
    });

    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (credentials: LoginForm) => {
    setLoading(true);
    clearError();
    loginMutation.mutate(credentials);
  };

  const register = (userData: RegisterForm) => {
    // Registro deshabilitado en frontend: informar al usuario en lugar de intentar la petición
    clearError();
    setError('Registro deshabilitado. Contacta al administrador.');
    setLoading(false);
    // No llamar a registerMutation
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  return {
    user: user,
    isAuthenticated,
    isLoading: isLoading || loginMutation.isPending || registerMutation.isPending || registerWithPersonalMutation.isPending,
    error,
    login,
    register,
    registerWithPersonal: (data: ExtendedRegisterForm) => {
      // Registro deshabilitado: mostrar mensaje en UI
      clearError();
      setError('Registro deshabilitado. Contacta al administrador.');
      setLoading(false);
      return;
    },
    logout,
    clearError,
    bloquearAccesoPlataforma,
  };
};
